import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faMobileAlt,
  faImage,
  faShareAlt,
  faSpinner,
  faShieldAlt,
  faArrowLeft,
  faArrowRight,
  faExclamationTriangle,
  faCheckCircle,
  faInbox,
} from "@fortawesome/free-solid-svg-icons";
import MemberViewAPI from "../../services/MemberViewAPI";
import MemberAnalysisDetailSheet from "./MemberAnalysisDetailSheet";
import { useTranslation } from "../../../../i18n/LanguageContext";

const ANALYSIS_TAB_DEFS = [
  {
    id: "EMAIL_ANALYSIS",
    icon: faEnvelope,
    labelKey: "usermgmt.analyses.tabEmails",
  },
  {
    id: "SMS_ANALYSIS",
    icon: faMobileAlt,
    labelKey: "usermgmt.analyses.tabTexts",
  },
  {
    id: "SOCIAL_MEDIA_ANALYSIS",
    icon: faShareAlt,
    labelKey: "usermgmt.analyses.tabSocialMedia",
  },
  {
    id: "IMAGE_ANALYSIS",
    icon: faImage,
    labelKey: "usermgmt.analyses.tabImages",
  },
];

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function AnalysisItem({ item, onClick }) {
  const { t } = useTranslation();
  return (
    <button
      className="um-analysis-item"
      onClick={() => onClick(item)}
      type="button"
    >
      <div className="um-analysis-item-main">
        <div className="um-analysis-item-top">
          <span
            className={`um-analysis-scam-badge ${item.isScam ? "scam" : "safe"}`}
          >
            <FontAwesomeIcon
              icon={item.isScam ? faExclamationTriangle : faCheckCircle}
            />
            {item.isScam
              ? t("usermgmt.analyses.scamDetected")
              : t("usermgmt.analyses.appearsSafe")}
          </span>
          {item.riskLevel && (
            <span
              className={`um-risk-level-badge risk-${(item.riskLevel || "").toLowerCase()}`}
            >
              {item.riskLevel}
            </span>
          )}
        </div>
        <p className="um-analysis-item-title">
          {item.analysisTitle || t("usermgmt.analyses.untitled")}
        </p>
        {item.analysisDetail && (
          <p className="um-analysis-item-detail">{item.analysisDetail}</p>
        )}
      </div>
      <div className="um-analysis-item-right">
        <span className="um-analysis-item-date">
          {formatDate(item.createdAt)}
        </span>
        <FontAwesomeIcon
          icon={faArrowRight}
          className="um-analysis-item-chevron"
        />
      </div>
    </button>
  );
}

function MemberAnalysesPanel({ member, onBack }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("EMAIL_ANALYSIS");
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const ANALYSIS_TABS = ANALYSIS_TAB_DEFS.map((tab) => ({
    ...tab,
    label: t(tab.labelKey),
  }));

  const loadAnalyses = useCallback(
    async (type, p = 0) => {
      if (!member?.uuid) return;
      setLoading(true);
      setError(null);
      try {
        let data;
        if (type === "SMS_ANALYSIS") {
          const res = await MemberViewAPI.getMemberForwardedTexts(
            member.uuid,
            p,
            20,
          );
          const raw = res?.data || res;
          // Map ForwardedSmsAnalysis fields to the shared AnalysisItem shape
          const mapped = (raw?.analyses || []).map((item) => ({
            // Display fields used by AnalysisItem
            id: item.id,
            isScam: item.isScam,
            riskLevel: item.riskLevel,
            confidence: item.confidence,
            analysisTitle: item.isScam
              ? t("usermgmt.analyses.smsScamDetected")
              : t("usermgmt.analyses.smsAnalyzed"),
            analysisDetail: item.senderPhone
              ? `From: ${item.senderPhone}`
              : item.messageContent?.slice(0, 80),
            createdAt: item.analyzedAt,
            // Full fields passed through for the detail sheet
            messageContent: item.messageContent,
            senderPhone: item.senderPhone,
            userContext: item.userContext,
            scamType: item.scamType,
            analysisExplanation: item.analysisExplanation,
            suggestions: item.suggestions,
            riskFactors: item.riskFactors,
            analysisResultJson: item.analysisResultJson,
            reportId: item.reportId,
          }));
          setAnalyses(mapped);
          setTotalPages(raw?.totalPages || 0);
          setPage(raw?.currentPage ?? p);
        } else if (type === "EMAIL_ANALYSIS") {
          const res = await MemberViewAPI.getMemberForwardedEmails(
            member.uuid,
            p,
            20,
          );
          const raw = res?.data || res;
          // Map EmailAnalysisRecord fields to the shared AnalysisItem shape.
          // NOTE: EmailAnalysisRecord wraps all analysis data inside `analysisResult`
          // (a ScamAnalysisResult object). Top-level convenience getters (isScam,
          // confidence) are delegated there; all other analysis fields must be read
          // from item.analysisResult.* directly.
          const mapped = (raw?.analyses || []).map((item, index) => ({
            // Display fields used by AnalysisItem
            id: item.analysisKey || `email-${index}`,
            isScam: item.analysisResult?.isScam,
            riskLevel: null,
            confidence: item.analysisResult?.confidence,
            analysisTitle: item.analysisResult?.subject
              ? item.analysisResult.subject
              : item.analysisResult?.isScam
                ? t("usermgmt.analyses.emailScamDetected")
                : t("usermgmt.analyses.emailAnalyzed"),
            analysisDetail: item.analysisResult?.sender
              ? `From: ${item.analysisResult.sender}`
              : null,
            createdAt: item.analysisResult?.analyzedAt,
            // Full fields passed through for the detail sheet (buildEmailAnalysis shape)
            explanation: item.analysisResult?.explanation,
            suggestions: item.analysisResult?.suggestions,
            riskFactors: item.analysisResult?.suspiciousElements,
            scamType: item.analysisResult?.scamType,
            analysisInput: item.analysisResult?.analyzedEmailContent?.body,
            reportId: item.analysisResult?.reportId,
            // Metadata fields for the detail-sheet header
            senderEmail: item.analysisResult?.sender,
            senderName: null,
            subject: item.analysisResult?.subject,
          }));
          setAnalyses(mapped);
          setTotalPages(raw?.totalPages || 0);
          setPage(raw?.currentPage ?? p);
        } else if (type === "SOCIAL_MEDIA_ANALYSIS") {
          const res = await MemberViewAPI.getMemberSocialMediaAnalyses(
            member.uuid,
            p,
            20,
          );
          const raw = res?.data || res;
          // Map SocialMediaAnalysis fields to the shared AnalysisItem shape
          const mapped = (raw?.analyses || []).map((item) => ({
            id: item.id,
            isScam: item.isScam,
            riskLevel: null,
            confidence: item.confidence,
            analysisTitle: item.platform
              ? `${item.platform}${item.username ? ` — @${item.username}` : ""}`
              : item.isScam
                ? t("usermgmt.analyses.socialMediaScamDetected")
                : t("usermgmt.analyses.socialMediaAnalyzed"),
            analysisDetail: item.postContent
              ? item.postContent.slice(0, 80)
              : null,
            createdAt: item.analyzedAt,
            // Full fields passed through for the detail sheet
            explanation: item.analysisExplanation,
            suggestions: item.suggestions,
            riskFactors: item.riskFactors,
            scamType: item.scamType,
            analysisInput: item.postContent,
            reportId: null,
            // Social-media-specific fields for AnalysisDetails component
            businessName: item.businessName,
            businessWebsite: item.businessWebsite,
            analysisStatus: item.analysisStatus,
            platform: item.platform,
            username: item.username,
          }));
          setAnalyses(mapped);
          setTotalPages(raw?.totalPages || 0);
          setPage(raw?.currentPage ?? p);
        } else if (type === "IMAGE_ANALYSIS") {
          const res = await MemberViewAPI.getMemberUploadedImages(
            member.uuid,
            p,
            20,
          );
          const raw = res?.data || res;
          // Map UploadedImageAnalysis fields to the shared AnalysisItem shape
          const mapped = (raw?.analyses || []).map((item) => ({
            id: item.id,
            isScam: item.isScam,
            riskLevel: null,
            confidence: item.confidence,
            analysisTitle: item.fileName
              ? item.fileName
              : item.isScam
                ? t("usermgmt.analyses.imageScamDetected")
                : t("usermgmt.analyses.imageAnalyzed"),
            analysisDetail: item.scamType ? item.scamType : null,
            createdAt: item.analyzedAt,
            // Full fields passed through for the detail sheet
            explanation: item.analysisExplanation,
            suggestions: item.suggestions,
            riskFactors: item.riskFactors,
            scamType: item.scamType,
            analysisInput: item.fileName,
            reportId: null,
          }));
          setAnalyses(mapped);
          setTotalPages(raw?.totalPages || 0);
          setPage(raw?.currentPage ?? p);
        } else {
          const res = await MemberViewAPI.getMemberAnalyses(
            member.uuid,
            type,
            p,
            20,
          );
          data = res?.data || res;
          setAnalyses(data?.analyses || []);
          setTotalPages(data?.totalPages || 0);
          setPage(data?.currentPage ?? p);
        }
      } catch {
        setError(t("usermgmt.analyses.loadFailed"));
      } finally {
        setLoading(false);
      }
    },
    [member?.uuid, t],
  );

  useEffect(() => {
    setAnalyses([]);
    setPage(0);
    setSelectedAnalysis(null);
    loadAnalyses(activeTab, 0);
  }, [activeTab, loadAnalyses]);

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    setActiveTab(tabId);
  };

  const memberName = member
    ? `${member.firstName} ${member.lastName}`
    : "Member";

  const activeTabLabel =
    ANALYSIS_TABS.find((tab) => tab.id === activeTab)?.label ?? "Analyses";

  return (
    <div className="um-analyses-panel">
      {/* Panel header */}
      <div className="um-analyses-panel-header">
        <button
          className="um-analyses-back-btn"
          onClick={onBack}
          type="button"
          aria-label={t("usermgmt.analyses.backToActivities")}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {t("usermgmt.analyses.backToActivities")}
        </button>
        <div className="um-analyses-panel-title-row">
          <FontAwesomeIcon
            icon={faShieldAlt}
            className="um-analyses-panel-icon"
          />
          <h3 className="um-analyses-panel-title">
            {t("usermgmt.analyses.panelTitle").replace("{name}", memberName)}
          </h3>
          <span className="um-analyses-readonly-badge">
            {t("usermgmt.analyses.readOnly")}
          </span>
        </div>
      </div>

      {/* Sub-tabs */}
      <div
        className="um-tab-bar um-analyses-subtabs"
        role="tablist"
        aria-label="Analysis type"
      >
        {ANALYSIS_TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            tabIndex={activeTab === tab.id ? 0 : -1}
            className={`um-tab${activeTab === tab.id ? " active" : ""}`}
            onClick={() => handleTabChange(tab.id)}
          >
            <FontAwesomeIcon icon={tab.icon} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content area */}
      <div className="um-analyses-content">
        {loading ? (
          <div className="um-analyses-loading">
            <FontAwesomeIcon icon={faSpinner} spin />
            <span>
              {t("usermgmt.analyses.loading").replace(
                "{type}",
                activeTabLabel.toLowerCase(),
              )}
            </span>
          </div>
        ) : error ? (
          <div className="um-analyses-error">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{error}</span>
            <button
              className="common-button small secondary"
              onClick={() => loadAnalyses(activeTab, page)}
              type="button"
            >
              {t("usermgmt.analyses.retry")}
            </button>
          </div>
        ) : analyses.length === 0 ? (
          <div className="um-analyses-empty">
            <FontAwesomeIcon
              icon={
                ANALYSIS_TABS.find((tab) => tab.id === activeTab)?.icon ??
                faInbox
              }
              className="um-analyses-empty-icon"
            />
            <p>
              {t("usermgmt.analyses.empty")
                .replace("{type}", activeTabLabel.toLowerCase())
                .replace("{name}", memberName)}
            </p>
          </div>
        ) : (
          <>
            <div className="um-analyses-list">
              {analyses.map((item) => (
                <AnalysisItem
                  key={item.id}
                  item={item}
                  onClick={setSelectedAnalysis}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="um-analyses-pagination">
                <button
                  className="common-button small secondary"
                  disabled={page === 0}
                  onClick={() => loadAnalyses(activeTab, page - 1)}
                  type="button"
                >
                  {t("usermgmt.analyses.previous")}
                </button>
                <span className="um-analyses-page-indicator">
                  {t("usermgmt.analyses.pageOf")
                    .replace("{page}", page + 1)
                    .replace("{total}", totalPages)}
                </span>
                <button
                  className="common-button small secondary"
                  disabled={page >= totalPages - 1}
                  onClick={() => loadAnalyses(activeTab, page + 1)}
                  type="button"
                >
                  {t("usermgmt.analyses.next")}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail side sheet */}
      {selectedAnalysis && (
        <MemberAnalysisDetailSheet
          analysis={selectedAnalysis}
          analysisType={activeTab}
          onClose={() => setSelectedAnalysis(null)}
        />
      )}
    </div>
  );
}

export default MemberAnalysesPanel;
